import {assert} from 'Asserts';
import * as IdleTimeoutDAO from 'auth/idle-timeout/IdleTimeoutDAO';
import IdleTimeoutWarningModal, {
  IDLE_TIMEOUT_WARNING_MODAL_KEY,
} from 'auth/idle-timeout/IdleTimeoutWarningModal';
import {
  bindChannelListener,
  bindEventListeners,
  broadcastToTabs,
  TimeoutEvent,
} from 'auth/idle-timeout/Listeners';
import {redirectToSessionExpired, signOut} from 'auth/signOut';
import {isBrowser, supportsBroadcastChannel} from 'lib/Browser';
import EntityStore from 'stores/EntityStore';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import legacyRunModal from 'ui-library/legacyRunModal';
import throttle from 'util/throttle';

const HEARTBEAT_TIME_MS = 60000;
const WARNING_TIME_MS = 120000;
assert(
  WARNING_TIME_MS > HEARTBEAT_TIME_MS,
  `Warning time ${WARNING_TIME_MS} must be greater than heartbeat time ${HEARTBEAT_TIME_MS}`
);

const THROTTLE_TIME_MS = 500;
const FINAL_BUFFER_MS = 2000;

const IDLE_TIMEOUT_CHANNEL = 'benchling.userSession.timeoutChannel';

let finalTimeoutTimer: ReturnType<typeof setTimeout>;
let warningTimer: ReturnType<typeof setTimeout>;
let inWarningState = false;
let localLastActive = Date.now();

// Time at which, if no further activity is detected, the server will time the user out. Set to null if the
// timeout is unknown, or if the feature is disabled.
let projectedTimeout: number | null = null;

// Handle messages from other tabs about when to redirect and show/close the warning modal
const tc: BroadcastChannel | null =
  isBrowser && supportsBroadcastChannel ? new BroadcastChannel(IDLE_TIMEOUT_CHANNEL) : null;

export function handleTimeoutEvent(ev: TimeoutEvent): void {
  // Ignore messages on signed out pages
  if (EntityStore.currentUser() == null) {
    return;
  }

  if (ev.data.timeout === true) {
    redirectToSessionExpired();
  }

  if (ev.data.warningDelay) {
    setWarningTimeout(ev.data.warningDelay);
  }

  if (ev.data.closeWarningModal) {
    inWarningState = false;
    LegacyModalContainer.close(IDLE_TIMEOUT_WARNING_MODAL_KEY);
  }

  if (ev.data.signOut) {
    // Use an independent POST signout request for each tab so we don't have to worry about whether the backend
    // has finished the signout flow before we redirect
    signOut();
  }
}

export function setLastActive(currentTime: number): void {
  localLastActive = currentTime;
}

export function getLastActive(): number {
  return localLastActive;
}

/**
 * This call binds global event listeners that detects if a user is active.
 * If the user is not active after IDENTITY_IDLE_TIMEOUT_MINUTES, we will time out the user.
 */
export function initListeners(callback: (event: Event) => void = handleUserActivity): void {
  if (EntityStore.currentUser() == null) {
    return;
  }

  bindEventListeners(callback);

  setInterval(pingServer, HEARTBEAT_TIME_MS);

  bindChannelListener(tc, handleTimeoutEvent);

  setLastActive(Date.now());
}

export const handleUserActivity = throttle(
  (
    event: Event,
    warningState: boolean = inWarningState,
    currentTime: number = Date.now(),
    timeoutTime: number | null = projectedTimeout
  ) => {
    // If the warning modal is open, only pressing "Continue" counts as activity.
    if (warningState) {
      return;
    }
    setLastActive(currentTime);

    // If activity is detected within HEARTBEAT_TIME_MS of when we expect to open the warning modal,
    // ping server immediately instead of on the interval. Otherwise, the server will time the user
    // out even if they are active because another heartbeat was not sent in time.
    if (timeoutTime && currentTime > timeoutTime - (HEARTBEAT_TIME_MS + WARNING_TIME_MS)) {
      pingServer(currentTime);
    }
  },
  THROTTLE_TIME_MS
);

// Ping server with user's last active time and get back seconds until timeout
export async function pingServer(currentTime: number = Date.now()): Promise<void> {
  const secondsSinceLastActive = Math.round((currentTime - getLastActive()) / 1000);
  const {secondsUntilTimeout} = await IdleTimeoutDAO.setClientLastActive(secondsSinceLastActive);
  if (secondsUntilTimeout == null) {
    projectedTimeout = null;
  } else {
    handleUpdatedProjectedTimeout(secondsUntilTimeout);
  }
}

export function handleUpdatedProjectedTimeout(
  secondsUntilTimeout: number,
  warningState: boolean = inWarningState
): void {
  projectedTimeout = Date.now() + secondsUntilTimeout * 1000;
  // Use secondsUntilTimeout to display a warning modal preceding timeout on all tabs
  if (!warningState) {
    const warningDelay = Math.max(0, secondsUntilTimeout * 1000 - WARNING_TIME_MS);
    setWarningTimeout(warningDelay);
    broadcastToTabs(tc, {warningDelay});
  }

  // Final ping to server to make sure user gets logged out and redirected immediately after timeout
  clearTimeout(finalTimeoutTimer);
  finalTimeoutTimer = setTimeout(pingServer, secondsUntilTimeout * 1000 + FINAL_BUFFER_MS);
}

// Show warning modal after a certain delay
export function setWarningTimeout(delay: number): void {
  clearTimeout(warningTimer);
  warningTimer = setTimeout(() => {
    legacyRunModal(IdleTimeoutWarningModal, {
      onRequestClose: continueSession,
      onSignOut: () => {
        broadcastToTabs(tc, {signOut: true});
        signOut();
      },
      modalKey: IDLE_TIMEOUT_WARNING_MODAL_KEY,
    });
    inWarningState = true;
  }, delay);
}

// If a user elects to continue their session from the modal, close all other modals and reset constants
export function continueSession(currentTime: number = Date.now()): void {
  inWarningState = false;
  broadcastToTabs(tc, {closeWarningModal: true});
  setLastActive(currentTime);
  pingServer();
}
